import { CrmClientAccount } from '@/components/~advisor/Details/UserDetails/types';
import { apiConfig } from '@oneview/config';
import { apiDatePattern } from '@oneview/constants/datePattern';
import { createQueryFn } from '@oneview/redux/utils';
import {
    AcatAccountContraType,
    AcatTransferType,
    AccountCurrencyApproach,
    AxisBankLinkFormValues,
    BankAccountType,
    CorporateActionsType,
    OrderFilterType,
    TopPositionsType,
} from '@orbis-systems/orbis-api-middleware';
import { showToast } from '@orbis-systems/orbis-react-toolkit';
import { sub } from 'date-fns';
import { format } from 'date-fns-tz';
import { flatMap } from 'lodash';
import { api } from './baseApi';
import { DataTags } from './dataTags';
import { TransferSchemaFormValues } from '@oneview/tables/MoneyTransferTable/schema';
import { AcatTypeEnum } from '@/components/~shared/~tables/AcatsTable/Acat.type';

export interface AccountInfoData {
    id: string;
    question: string;
    answer: string;
}

export interface AccountInfo {
    application: {
        suitability: {
            investment: {
                objective: AccountInfoData;
                experience: AccountInfoData;
            };
            liquidity: AccountInfoData;
            'annual-income': AccountInfoData;
            'net-worth': {
                liquid: AccountInfoData;
                total: AccountInfoData;
            };
            'risk-tolerance': AccountInfoData;
            'time-horizon': AccountInfoData;
        };
    };
    applicant: {
        primary: {
            name?: {
                first?: AccountInfoData;
                middle?: AccountInfoData;
                last?: AccountInfoData;
            };
            citizenship?: {
                country?: AccountInfoData;
            };
            employment?: {
                status?: AccountInfoData;
                employer?: AccountInfoData;
                position?: AccountInfoData;
            };
            address?: {
                home?: {
                    line?: [null, AccountInfoData, AccountInfoData];
                    region?: AccountInfoData;
                    state?: AccountInfoData;
                    zip?: AccountInfoData;
                    country?: AccountInfoData;
                };
            };
            phone?: {
                home?: AccountInfoData;
            };
            email?: {
                personal?: AccountInfoData;
            };
        };
    };
}

export type AcatAssetType = { symbol: string; quantity: number; type: AcatTypeEnum };

export type AcatPayloadType<T = AcatAssetType> = {
    accountNumber: string;
    type: AcatTransferType;
    contraAccount: string;
    contraAccountType: AcatAccountContraType;
    contraTitle: string;
    primaryTaxId: string;
    secondaryTaxId?: string;
    participantNumber: string;
    comment?: string;
    assets: T[];
};

export const accountApi = api.injectEndpoints({
    endpoints: (build) => ({
        getAccounts: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.account.getAccounts()),
            providesTags: [DataTags.GetAccounts],
        }),
        getAccount: build.query({
            queryFn: createQueryFn((accountId: number, { extra: api }) =>
                api.orbisMiddleware.account.getAccount(accountId),
            ),
        }),
        getAccountHistory: build.query({
            queryFn: createQueryFn(
                (
                    {
                        accountIds,
                        dateFrom,
                        dateTo,
                    }: {
                        accountIds: number[];
                        dateFrom: Date;
                        dateTo: Date;
                    },
                    { extra: api },
                ) => api.orbisMiddleware.account.getAccountHistory(accountIds, dateFrom, dateTo),
            ),
        }),
        getBranchAccounts: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.branch.getAccounts()),
        }),
        getBranchAccount: build.query({
            queryFn: createQueryFn((accountId: number, { extra: api }) =>
                api.orbisMiddleware.branch.getAccount(accountId),
            ),
            providesTags: [DataTags.GetBranchAccount],
        }),
        getAccountPortfolio: build.query({
            queryFn: createQueryFn(
                (
                    { accountId, symbol, currency }: { accountId: number; symbol?: string; currency?: string },
                    { extra: api },
                ) => api.orbisMiddleware.account.getPortfolio({ accountId, symbol, currency }),
            ),
            providesTags: [DataTags.GetAccountPortfolio],
        }),
        getAccountOrders: build.query({
            queryFn: createQueryFn(
                (
                    {
                        accountId,
                        orderFilter,
                        symbol,
                    }: {
                        accountId: number;
                        orderFilter?: OrderFilterType | OrderFilterType[];
                        symbol?: string;
                    },
                    { extra: api },
                ) =>
                    api.orbisMiddleware.account.getOrders(
                        accountId,
                        orderFilter
                            ? orderFilter
                            : [OrderFilterType.Latest, OrderFilterType.Open, OrderFilterType.Today],
                        symbol,
                    ),
            ),
            providesTags: [DataTags.GetAccountOrders],
        }),
        getAccountBuyingPower: build.query({
            queryFn: createQueryFn((accountId: number | string, { extra: api }) =>
                api.orbisMiddleware.account.getAccountBuyingPower(accountId, AccountCurrencyApproach.PerCurrency),
            ),
            providesTags: [DataTags.GetAccountBuyingPower],
        }),
        getAccountCombinedBalances: build.query({
            queryFn: createQueryFn(
                ({ accountId, currency }: { accountId: number | string; currency?: string }, { extra: api }) =>
                    api.orbisMiddleware.account.getAccountCombinedBalance(accountId, currency),
            ),
        }),
        getAccountAvailableCashForWithdrawal: build.query({
            queryFn: createQueryFn((accountNumber: string, { extra: api }) =>
                api.orbisMiddleware.account.getAccountWithdrawableCash(accountNumber),
            ),
            providesTags: [DataTags.GetAccountAvailableCashForWithdrawal],
        }),
        getAccountMarketCapBreakdown: build.query({
            queryFn: createQueryFn((accountId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getAccountMarketCapBreakdownStat(accountId),
            ),
        }),
        getAccountSectorsBreakdown: build.query({
            queryFn: createQueryFn((accountId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getAccountSectorsBreakdownStat(accountId),
            ),
        }),
        getAccountTopPositionsBreakdown: build.query({
            queryFn: createQueryFn(
                (
                    { accountId, by = TopPositionsType.Weight }: { accountId: number; by?: TopPositionsType },
                    { extra: api },
                ) => api.orbisMiddleware.stat.getAccountTopPositionsStat(accountId, by),
            ),
        }),
        getAccountModelAndGroups: build.query({
            queryFn: createQueryFn((accountId: number, { extra: api }) =>
                api.orbisMiddleware.account.getModelAndGroups(accountId),
            ),
        }),
        getInventoryAccounts: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.branch.getInventoryAccounts()),
        }),
        getAccountHoldingInfo: build.query({
            queryFn: createQueryFn(
                (
                    { accountId, symbol, currency }: { accountId: number; symbol: string; currency?: string },
                    { extra: api },
                ) => api.orbisMiddleware.account.getHoldingInfo(accountId, symbol, currency),
            ),
        }),
        getAccountPerformance: build.query({
            queryFn: createQueryFn(
                ({ accountId, currency }: { accountId: number; currency?: string }, { extra: api }) =>
                    api.orbisMiddleware.account.getAccountPerformance(
                        accountId,
                        format(sub(new Date(), { years: 2 }), apiDatePattern),
                        true,
                        currency,
                    ),
            ),
        }),
        getAcatTransfer: build.query({
            queryFn: createQueryFn((accountId: number, { extra: api }) =>
                api.orbisMiddleware.account.getAcatTransfer(accountId),
            ),
            providesTags: [DataTags.GetAccountAcats],
        }),
        initiateAcatTransfer: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        accountNumber,
                        type,
                        contraAccount,
                        contraAccountType,
                        contraTitle,
                        primaryTaxId,
                        secondaryTaxId,
                        participantNumber,
                        comment,
                        assets,
                    }: AcatPayloadType<Partial<AcatAssetType>>,
                    { extra: api, dispatch },
                ) => {
                    const response = await api.orbisMiddleware.account.initiateAcatTransfer({
                        accountNumber,
                        type,
                        contraAccount,
                        contraAccountType,
                        contraTitle,
                        primaryTaxId,
                        secondaryTaxId,
                        participantNumber,
                        comment,
                        assets: assets as AcatAssetType[],
                    });

                    dispatch(showToast('ACAT transfer was initiated'));
                    return response.data;
                },
                {
                    pendingMessage: 'Initiating ACAT transfer…',
                    alertIfError: true,
                },
            ),
            invalidatesTags: [DataTags.GetAccountAcats],
        }),
        cancelAcatTransfer: build.mutation({
            queryFn: createQueryFn(async (requestId: string, { extra: api, dispatch }) => {
                const response = await api.orbisMiddleware.account.cancelAcatTransfer(requestId);
                dispatch(showToast('ACAT transfer was canceled'));
                return response.data;
            }),
            invalidatesTags: [DataTags.GetAccountAcats],
        }),
        // Plaid - linking bank account via plaid
        getTempIavKey: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.account.getTempIavKey()),
        }),
        iavComplete: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        accountNumber,
                        publicToken,
                        bankAccountId,
                    }: { accountNumber: string; publicToken: string; bankAccountId: string },
                    { extra: api, dispatch },
                ) => {
                    const response = await api.orbisMiddleware.account.iavComplete(
                        accountNumber,
                        publicToken,
                        bankAccountId,
                    );
                    dispatch(showToast('Bank link was added'));
                    return response;
                },
                {
                    pendingMessage: 'Adding bank link…',
                    alertIfError: true,
                },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        getBankLinks: build.query({
            queryFn: createQueryFn(
                async ({ accountNumber }: { accountNumber?: string; allAccounts?: boolean }, { extra: api }) => {
                    const data = accountNumber
                        ? await api.orbisMiddleware.account.getAccountBankLinks(accountNumber)
                        : await api.orbisMiddleware.account.getAccountBankLinks();

                    return accountNumber ? data : flatMap(data, (link) => link);
                },
            ),
            providesTags: [DataTags.GetBankLinks],
        }),
        createBankLinks: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        accountNumber,
                        bankRoutingNumber,
                        bankAccount,
                        bankAccountOwnerName,
                        bankAccountType,
                        nickname,
                    }: {
                        accountNumber: string;
                        bankRoutingNumber: string;
                        bankAccount: string;
                        bankAccountOwnerName: string;
                        bankAccountType: BankAccountType;
                        nickname: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    const data = await api.orbisMiddleware.account.createAchBankLink({
                        accountNumber,
                        confirmBankAccount: accountNumber,
                        bankRoutingNumber,
                        bankAccount,
                        bankAccountOwnerName,
                        bankAccountType,
                        nickname,
                    });

                    dispatch(showToast('Bank link was created'));
                    return data;
                },
                { pendingMessage: 'Creating bank link…' },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        createIfscBankLinks: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        bankAccount,
                        ifscCode,
                        beneficiaryName,
                        account,
                        international,
                    }: {
                        bankAccount: number;
                        ifscCode: string;
                        beneficiaryName: string;
                        account: string;
                        international?: boolean;
                    },
                    { extra: api, dispatch },
                ) => {
                    const data = await api.orbisMiddleware.account.createIfscBankLink({
                        bankAccount,
                        ifscCode,
                        beneficiaryName,
                        account,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        international,
                    });

                    dispatch(showToast('Bank link was created'));
                    return data;
                },
                { pendingMessage: 'Creating bank link…' },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        initiateAxisBankLinkTransfer: build.mutation({
            queryFn: createQueryFn(
                async ({ values, entryId }: { values: AxisBankLinkFormValues; entryId: number }, { extra: api }) => {
                    const data = await api.orbisMiddleware.account.initiateAxisBankLinkTransfer({ ...values, entryId });
                    return data;
                },
                { pendingMessage: 'Redirecting to Axis…' },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        renameBankLink: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        entryId,
                        accountNumber,
                        nickname,
                    }: {
                        entryId: number;
                        accountNumber: string;
                        nickname: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    const data = await api.orbisMiddleware.account.renameBankLink(accountNumber, entryId, nickname);
                    dispatch(showToast('Bank link was renamed'));
                    return data;
                },
                { pendingMessage: 'Renaming bank link…' },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        cancelBankLink: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        entryId,
                        accountNumber,
                    }: {
                        entryId: number;
                        accountNumber: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    const data = await api.orbisMiddleware.account.cancelBankLink(accountNumber, entryId);

                    dispatch(showToast('Bank link was deleted'));
                    return data;
                },
                { pendingMessage: 'Canceling bank link…' },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        cancelIfscBankLink: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        id,
                        account,
                        reason,
                    }: {
                        id: number;
                        account: string;
                        reason?: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    const data = await api.orbisMiddleware.account.cancelIfscBankLink(id, account, reason);

                    dispatch(showToast('Bank link was deleted'));
                    return data;
                },
                { pendingMessage: 'Canceling bank link…' },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        confirmBankLink: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        accountNumber,
                        entryId,
                        amount1,
                        amount2,
                    }: { accountNumber: string; entryId: number; amount1: number; amount2: number },
                    { extra: api, dispatch },
                ) => {
                    const data = await api.orbisMiddleware.account.approveBankLink({
                        accountNumber,
                        entryId,
                        amount1,
                        amount2,
                    });
                    dispatch(showToast('Bank link was confirmed'));
                    return data;
                },
                { pendingMessage: 'Confirming micro-deposits…' },
            ),
            invalidatesTags: [DataTags.GetBankLinks],
        }),
        getAccountPositionTaxLots: build.query({
            queryFn: createQueryFn(
                (
                    { accountId, symbol, option }: { accountId: number; symbol: string; option: boolean },
                    { extra: api },
                ) => api.orbisMiddleware.account.getOrders(accountId, OrderFilterType.Cost, symbol, option),
            ),
        }),
        getCorporateActions: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        dateFrom,
                        dateTo,
                        accountId,
                        symbols,
                        types,
                    }: {
                        dateFrom: string;
                        dateTo: string;
                        accountId?: number;
                        symbols?: string[];
                        types?: CorporateActionsType;
                    },
                    { extra: api },
                ) =>
                    accountId
                        ? api.orbisMiddleware.account.getCorporateActions(accountId, dateFrom, dateTo, types)
                        : api.orbisMiddleware.quote.getCorporateActions(symbols || [], dateFrom, dateTo, types),
            ),
        }),
        getMoneyTransfers: build.query({
            queryFn: createQueryFn(
                (
                    {
                        accountNumber,
                        params,
                    }: {
                        accountNumber?: string;
                        params?: { start?: number; perPage?: number };
                    },
                    { extra: api },
                ) =>
                    accountNumber
                        ? api.orbisMiddleware.account.getAccountTransfers(accountNumber)
                        : api.orbisMiddleware.branch.getBranchMoneyTransfers(params?.start, params?.perPage),
            ),
            providesTags: [DataTags.GetMoneyTransfers],
        }),
        cancelTransfer: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        accountNumber,
                        transferId,
                    }: {
                        accountNumber: string;
                        transferId: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    const data = await api.orbisMiddleware.account.cancelTransaction(accountNumber, transferId);

                    dispatch(showToast('Transfer was canceled'));
                    return data;
                },
                { pendingMessage: 'Canceling transfer…' },
            ),
            invalidatesTags: [DataTags.GetMoneyTransfers, DataTags.GetMoneyTransfers],
        }),
        deposit: build.mutation({
            queryFn: createQueryFn(
                async (values: TransferSchemaFormValues, { extra: api, dispatch }) => {
                    const data = await api.orbisMiddleware.account.depositCash({
                        ...values,
                        contribution: {
                            type: values.contributionType,
                            priorYear: values.contributionPriorYear || false,
                        },
                    });

                    dispatch(showToast('Money was deposited'));
                    return data;
                },
                { pendingMessage: 'Depositing money…' },
            ),
            invalidatesTags: [DataTags.GetMoneyTransfers],
        }),
        withdraw: build.mutation({
            queryFn: createQueryFn(
                async (values: TransferSchemaFormValues, { extra: api, dispatch }) => {
                    const data = await api.orbisMiddleware.account.withdrawCash({
                        ...values,
                        distribution: {
                            institution: values.institution,
                            reason: values.reason,
                            federalTax: {
                                type: values.federalTaxType,
                                amount: values.federalTaxAmount || 0,
                            },
                            stateTax: {
                                type: values.stateTaxType,
                                amount: values.stateTaxAmount || 0,
                            },
                        },
                    });

                    dispatch(showToast('Money was withdrawn'));
                    return data;
                },
                { pendingMessage: 'Withdrawing money…' },
            ),
            invalidatesTags: [DataTags.GetMoneyTransfers],
        }),
        wireWithdraw: build.mutation({
            queryFn: createQueryFn(
                async (values: TransferSchemaFormValues, { extra: api, dispatch }) => {
                    const {
                        accountNumber,
                        amount,
                        recipientBankId,
                        recipientBankType,
                        beneficiaryAccount,
                        beneficiaryAccountType,
                    } = values;
                    const response = await api.orbisSdk.post('transfer/wire/outgoing', {
                        account: accountNumber,
                        amount,
                        international: false,
                        disbursementType: 'Partial',
                        recipientBank: {
                            identifier: { type: recipientBankType, id: recipientBankId },
                        },
                        beneficiary: {
                            account: beneficiaryAccount,
                            accountType: beneficiaryAccountType,
                            thirdParty: false,
                        },
                    });
                    dispatch(showToast('Wire withdrawal request submitted…'));
                    return response.data;
                },
                { pendingMessage: 'Wire withdrawal request submitted…' },
            ),
            invalidatesTags: [DataTags.GetMoneyTransfers],
        }),
        getAccountDriftReport: build.query({
            queryFn: createQueryFn((accountId: number, { extra: api }) =>
                api.orbisMiddleware.account.getDriftReport(accountId),
            ),
            providesTags: [DataTags.GetAccountDriftReport],
        }),
        applyAccountCharges: build.mutation({
            queryFn: createQueryFn(
                async ({ accountId, amount }: { accountId: number; amount: number }, { extra: api, dispatch }) =>
                    api.orbisMiddleware.account
                        .applyAccountCharges(accountId, amount)
                        .then(() => {
                            dispatch(showToast('Charge applied'));
                        })
                        .catch((error) => dispatch(showToast(error.orbisErrorMessage))),
                { pendingMessage: 'Applying charges…' },
            ),
        }),
        getAccountCharges: build.query({
            queryFn: createQueryFn(
                (
                    {
                        accountId,
                        dateFrom,
                        dateTo,
                        chargeRef,
                    }: { accountId: number; dateFrom?: Date; dateTo?: Date; chargeRef?: string },
                    { extra: api },
                ) => api.orbisMiddleware.account.getAccountCharges(accountId, dateFrom, dateTo, chargeRef),
            ),
        }),
        cancelAccountCharges: build.mutation({
            queryFn: createQueryFn(
                async (chargeRef: string, { extra: api, dispatch }) =>
                    api.orbisMiddleware.account
                        .cancelAccountCharges(chargeRef)
                        .then(() => {
                            dispatch(showToast('Charge cancelled'));
                        })
                        .catch((error) => dispatch(showToast(error.orbisErrorMessage))),
                { pendingMessage: 'Canceling charges…' },
            ),
        }),
        getAccountInfo: build.query({
            queryFn: createQueryFn(async (accountNumber: string, { extra: api }) => {
                const response = await api.middlewareSdk.get<Record<string, AccountInfo>>(
                    'crm/v1/accounts/:accountNumber',
                    {},
                    {
                        pathParams: { accountNumber },
                    },
                );
                return response.data;
            }),
        }),
        getUserCrmAccounts: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        accountNumbers,
                        userWatchmenId,
                    }: {
                        accountNumbers: string[];
                        userWatchmenId?: number;
                    },
                    { extra: api },
                ) => {
                    const { watchmenInstance: watchmenInstanceName } = apiConfig;
                    const { data } = await api.middlewareSdk.put<{ list: CrmClientAccount[] }>('crm/v1/accounts', {
                        watchmenInstanceName,
                        userWatchmenId: userWatchmenId?.toString(),
                        accountNumbers,
                    });

                    return data.list;
                },
            ),
        }),
    }),
});

export const {
    useGetBranchAccountQuery,
    useGetBranchAccountsQuery,
    useGetAccountsQuery,
    useGetAccountQuery,
    useGetAccountHistoryQuery,
    useLazyGetBranchAccountQuery,
    useGetAccountPortfolioQuery,
    useLazyGetAccountPortfolioQuery,
    useGetAccountBuyingPowerQuery,
    useGetAccountCombinedBalancesQuery,
    useGetMoneyTransfersQuery,
    useGetAccountOrdersQuery,
    useGetAccountAvailableCashForWithdrawalQuery,
    useLazyGetAccountMarketCapBreakdownQuery,
    useGetAccountSectorsBreakdownQuery,
    useGetAccountTopPositionsBreakdownQuery,
    useGetInventoryAccountsQuery,
    useGetAccountHoldingInfoQuery,
    useGetAccountModelAndGroupsQuery,
    useGetAccountPerformanceQuery,
    useGetAcatTransferQuery,
    useInitiateAcatTransferMutation,
    useGetTempIavKeyQuery,
    useIavCompleteMutation,
    useGetBankLinksQuery,
    useCreateIfscBankLinksMutation,
    useCancelIfscBankLinkMutation,
    useCreateBankLinksMutation,
    useRenameBankLinkMutation,
    useCancelBankLinkMutation,
    useConfirmBankLinkMutation,
    useGetAccountPositionTaxLotsQuery,
    useGetCorporateActionsQuery,
    useCancelTransferMutation,
    useDepositMutation,
    useWireWithdrawMutation,
    useWithdrawMutation,
    useLazyGetBranchAccountsQuery,
    useGetAccountDriftReportQuery,
    useApplyAccountChargesMutation,
    useGetAccountChargesQuery,
    useCancelAccountChargesMutation,
    useGetAccountInfoQuery,
    useCancelAcatTransferMutation,
    useGetUserCrmAccountsQuery,
    useInitiateAxisBankLinkTransferMutation,
} = accountApi;
